import React, {useEffect, useState} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {isMobile} from "react-device-detect";
import {useLocation, useNavigate} from "react-router-dom";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import {ROUTES} from "../config/routes";

import {LaneData} from "../components/LaneData";
import {PageLayout} from "../components/PageLayout";
import {PageLayoutMobile} from "../components/PageLayoutMobile";
import {CitySearchMobile} from "../components/CitySearchMobile";
import SearchTabs from "./SearchTabs";
import {Favorites} from "./Favorites";
import {Insights} from "./Insights";
import FileUploadProcessor from "./LaneDataFileUpload";
import Administration from "./Administration";
import Profile from "./Profile";
import RightDashboard from "./RightDashboard";
import {FreightLaneManager} from "./FreightLaneManager";
import UserService from "../services/UserService";
import TopLanes from "../components/TopLanes";
import PlanRoute from "../components/milemaker/PlanRoute";
import '../styles/Dashboard.css';

const adminUsers = [
    "andyc@farelanes.com",
    "andrew@farelanes.com",
    "andrew.cohen@farelanes.com",
    "neal.huffman@farelanes.com",
    "kapil.reddy@farelanes.com",
];

const Dashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [subscriptionType, setSubscriptionType] = useState("Default");
    const [defaultIndex, setDefaultIndex] = useState(0);
    const [apiLoaded, setApiLoaded] = useState(false);
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [profileSubTabIndex, setProfileSubTabIndex] = useState(0);
    const [onboardingStatus, setOnboardingStatus] = useState({
        profileIncomplete: false,
        companyIdIncomplete: false,
        loading: true
    });
    const {user, isAuthenticated, getAccessTokenSilently, isLoading: authLoading} = useAuth0();

    const reloadLayout = () => {
        setRefreshKey(prevKey => prevKey + 1);
    };

    const handleProfileDataChange = () => {
        setDefaultIndex(0);
        reloadLayout();
        fetchSubscriptionType();
        fetchOnboardingStatus(); // Refresh onboarding status after profile update
    };

    const fetchOnboardingStatus = async () => {
        if (authLoading || !isAuthenticated || !user) {
            return;
        }
        
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.userSettingsFlow(token, user);
            const data = response?.data;
            
            setOnboardingStatus({
                profileIncomplete: data?.screenOne === true,
                companyIdIncomplete: data?.screenTwo === true,
                loading: false
            });
        } catch (error) {
            console.error("Error fetching onboarding status:", error);
            setOnboardingStatus({
                profileIncomplete: false,
                companyIdIncomplete: false,
                loading: false
            });
        }
    };

    const fetchSubscriptionType = async () => {
        // Wait for authentication to be ready, especially after Stripe redirect
        if (authLoading || !isAuthenticated || !user) {
            return;
        }
        
        setApiLoaded(false);
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.verify(user, token);
            const subType = response?.data?.subscriptionType || "Default";
            setSubscriptionType(subType);
            setDefaultIndex(() => {
                if (subType === "Default") return 6;
                if (subType === "CoPilot") return 2;
                return 0;
            });
            setApiLoaded(true);
            reloadLayout();
        } catch (error) {
            console.error("Error fetching subscription type", error);
            // If auth error, don't fail silently - let AuthenticationGuard handle it
            if (error?.error === 'login_required') {
                throw error;
            }
        }
    };

    useEffect(() => {
        // Wait for auth to be ready before fetching subscription type
        if (!authLoading && isAuthenticated && user) {
            fetchSubscriptionType();
            fetchOnboardingStatus();
            reloadLayout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Note: defaultIndex is intentionally NOT in dependencies - it's set BY this effect, not a trigger FOR it
    }, [authLoading, isAuthenticated, user]);

    // Once onboarding status is known, if onboarding is incomplete,
    // make sure the visible tab is SETUP (index 6) with the right subtab.
    useEffect(() => {
        if (onboardingStatus.loading || !apiLoaded) return;

        const { profileIncomplete, companyIdIncomplete } = onboardingStatus;
        if (!profileIncomplete && !companyIdIncomplete) return;

        // Only adjust once when we're not already on SETUP
        if (currentTabIndex !== 6) {
            setCurrentTabIndex(6);
        }

        if (profileIncomplete && profileSubTabIndex !== 0) {
            setProfileSubTabIndex(0);
        } else if (!profileIncomplete && companyIdIncomplete && profileSubTabIndex !== 1) {
            setProfileSubTabIndex(1);
        }
    }, [onboardingStatus, apiLoaded, currentTabIndex, profileSubTabIndex]);

    // Check URL parameters for tab navigation (e.g., from Stripe Customer Portal return)
    // This should run IMMEDIATELY when location changes to take priority over defaultIndex
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tabParam = urlParams.get('tab');
        const subtabParam = urlParams.get('subtab');
        
        if (tabParam === 'setup') {
            console.log('URL parameter detected: tab=setup, subtab=' + subtabParam + ', location.search=' + location.search);
            // Setup tab is at index 6 - set immediately to override defaultIndex
            setCurrentTabIndex(6);
            
            // Map subtab names to indices
            if (subtabParam === 'subscriptions') {
                // Subscriptions sub-tab is at index 2 (0=Profile, 1=COMPANY ID, 2=Subscriptions, 3=Tickers & City Pairs)
                setProfileSubTabIndex(2);
                console.log('Setting subscriptions sub-tab to index 2');
            } else if (subtabParam === 'profile') {
                setProfileSubTabIndex(0);
            } else if (subtabParam === 'company') {
                setProfileSubTabIndex(1);
            } else if (subtabParam === 'tickers') {
                setProfileSubTabIndex(3);
            }
        }
    }, [location.search]); // Re-run when URL search params change
    
    // Update current tab index when defaultIndex changes
    // BUT only if there are no URL parameters (to avoid overriding URL-based navigation)
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tabParam = urlParams.get('tab');
        
        // Only set defaultIndex if no URL tab parameter is present AND API is loaded
        if (!tabParam && apiLoaded) {
            setCurrentTabIndex(defaultIndex);
        }
    }, [defaultIndex, apiLoaded, location.search]);

    // Listen for custom navigation events
    useEffect(() => {
        const handleNavigateToProfile = (event) => {
            const { mainTabIndex, subTabIndex } = event.detail;
            setCurrentTabIndex(mainTabIndex);
            setProfileSubTabIndex(subTabIndex);
        };

        const handleOnboardingStatusChanged = () => {
            fetchOnboardingStatus();
        };

        window.addEventListener('navigateToProfile', handleNavigateToProfile);
        window.addEventListener('onboardingStatusChanged', handleOnboardingStatusChanged);
        
        return () => {
            window.removeEventListener('navigateToProfile', handleNavigateToProfile);
            window.removeEventListener('onboardingStatusChanged', handleOnboardingStatusChanged);
        };
    }, [authLoading, isAuthenticated, user, getAccessTokenSilently]); // Include dependencies

    const isTabDisabled = (tab) => {
        const tabRules = {
            Dashboard: ["Default", "CoPilot"],
            MileMaker: ["Default", "CoPilot", "Silver","Gold"],
            RoutPlanning: ["Platinum", "Gold"],
            TopRates: ["Default", "CoPilot", "Silver"],
            RatesAndHistory: ["Default"],
            FavoriteLanes: ["Default", "Silver", "CoPilot"],
            Insights: ["Default", "Silver", "Gold", "CoPilot"],
            RFQ: ["Default", "Silver", "CoPilot"],
            Administration: ["Default"],
            News: ["Default"],
        };
        return tabRules[tab]?.includes(subscriptionType);
    };

    if (isMobile) {
        return (
            <PageLayoutMobile>
                <CitySearchMobile/>
            </PageLayoutMobile>
        );
    }

    // Check if user is on Setup tab with correct subtab
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const subtabParam = urlParams.get('subtab');
    const isOnSetupTab = currentTabIndex === 6; // Setup tab is at index 6
    // Also check URL params directly to ensure we catch navigation immediately
    const isOnProfileSubtab = isOnSetupTab && (profileSubTabIndex === 0 || subtabParam === 'profile');
    const isOnCompanySubtab = isOnSetupTab && (profileSubTabIndex === 1 || subtabParam === 'company');
    
    // Determine if we should show the blocker
    // Show blocker ONLY when user tries to access tabs OTHER than Setup
    // Allow full access to Setup tab and all its subtabs
    // Priority: Profile first, then Company ID
    const shouldShowBlocker = !onboardingStatus.loading && 
        !isOnSetupTab && 
        (onboardingStatus.profileIncomplete || onboardingStatus.companyIdIncomplete);
    
    const onboardingTooltip = onboardingStatus.profileIncomplete
        ? "Complete Setup → Profile first"
        : onboardingStatus.companyIdIncomplete
            ? "Complete Setup → Company ID"
            : "";

    const isBlockedByOnboarding = (tabIndex) =>
        !onboardingStatus.loading &&
        (onboardingStatus.profileIncomplete || onboardingStatus.companyIdIncomplete) &&
        tabIndex !== 6; // allow Setup tab

    return (
        <div>
            <PageLayout key={refreshKey}>
                {apiLoaded && (
                        <div className="dashboard-container">
                            <div className="row">
                                <div className="col-md-12">

                                    <Tabs 
                                        selectedIndex={currentTabIndex}
                                        onSelect={(index) => {
                                            // If onboarding incomplete, keep user in Setup and show tooltip on disabled tabs.
                                            if (isBlockedByOnboarding(index)) return;
                                            setCurrentTabIndex(index);
                                        }}
                                    >
                                    <TabList className="profile-root-tabs">
                                        <Tab
                                            disabled={isTabDisabled("Dashboard") || isBlockedByOnboarding(0)}
                                            title={isBlockedByOnboarding(0) ? onboardingTooltip : undefined}
                                        >
                                            Dashboard
                                        </Tab>
                                        <Tab
                                            disabled={isTabDisabled("MileMaker") || isBlockedByOnboarding(1)}
                                            title={isBlockedByOnboarding(1) ? onboardingTooltip : undefined}
                                        >
                      						<span data-toggle="tooltip" data-placement="top" title="Platinum Feature only">MileMaker</span>
                                        </Tab>
                                        <Tab
                                            disabled={isTabDisabled("RatesAndHistory") || isBlockedByOnboarding(2)}
                                            title={isBlockedByOnboarding(2) ? onboardingTooltip : undefined}
                                        >
                                            Rates & History
                                        </Tab>

                                        <Tab
                                            disabled={isTabDisabled("TopRates") || isBlockedByOnboarding(3)}
                                            title={isBlockedByOnboarding(3) ? onboardingTooltip : undefined}
                                        >
                                            Top Rates
                                        </Tab>
                               
                                        <Tab
                                            disabled={isTabDisabled("Insights") || isBlockedByOnboarding(4)}
                                            title={isBlockedByOnboarding(4) ? onboardingTooltip : undefined}
                                        >
                      						<span data-toggle="tooltip" data-placement="top" title="Platinum Feature">Insights</span>
                                        </Tab>
                                        <Tab
                                            disabled={isTabDisabled("RFQ") || isBlockedByOnboarding(5)}
                                            title={isBlockedByOnboarding(5) ? onboardingTooltip : undefined}
                                        >
                      						<span data-toggle="tooltip" data-placement="top" title="Gold & Platinum Feature">RFQ</span>
                                        </Tab>
                                        <Tab disabled={isTabDisabled("Setup")}>Setup</Tab>
                                        {isAuthenticated && adminUsers.includes(user?.email) && (
                                            <Tab
                                                disabled={isTabDisabled("Administration") || isBlockedByOnboarding(7)}
                                                title={isBlockedByOnboarding(7) ? onboardingTooltip : undefined}
                                            >
                                                Administration
                                            </Tab>
                                        )}
                                        <Tab
                                            disabled={isTabDisabled("News") || isBlockedByOnboarding(8)}
                                            title={isBlockedByOnboarding(8) ? onboardingTooltip : undefined}
                                        >
                                            News
                                        </Tab>
                                    </TabList>
                                    
                                    <TabPanel><FreightLaneManager/></TabPanel>
                                    <TabPanel><PlanRoute/></TabPanel>
                                    <TabPanel><SearchTabs subscriptionType={subscriptionType}/></TabPanel>

                                    <TabPanel className="v2-tab-content"><TopLanes/></TabPanel>
                                    <TabPanel><Insights/></TabPanel>
                                    <TabPanel><FileUploadProcessor/></TabPanel>
                                    <TabPanel>
                                        <Profile 
                                            onDataChange={handleProfileDataChange}
                                            defaultSubTabIndex={profileSubTabIndex}
                                            onSubTabChange={setProfileSubTabIndex}
                                        />
                                    </TabPanel>
                                    {isAuthenticated && adminUsers.includes(user?.email) && (
                                        <TabPanel><Administration/></TabPanel>
                                    )}
                                    <TabPanel><RightDashboard/></TabPanel>
                                </Tabs>
                                </div>
                            </div>
                        </div>
                )}
            </PageLayout>
        </div>
    );
};

export default Dashboard;